import {
  useState,
  useEffect,
  useRef,
  Fragment,
  type MouseEvent,
} from "react";
import { XCircle, PlusCircle, ChevronDown } from "lucide-react";
import { removeAccents } from "../utils/remove_accents";
import type { DropdownMultiTypeaheadUiStrings } from "../types/freelancer_modal_messages";

export type DropdownMultiTypeaheadProps<T> = {
  selected: T[];
  onChange: (selected: T[]) => void;
  fetchOptions?: (query: string) => Promise<T[]>;
  options?: T[];
  onSearchChange?: (query: string) => void;
  getOptionLabel: (item: T) => string;
  getOptionValue: (item: T) => string | number;
  placeholder?: string;
  className?: string;
  error?: boolean;
  hideSelected?: boolean;
  disabled?: boolean;
  onCreate?: (query: string) => Promise<void> | void;
  groupBy?: (item: T) => string;
  inputMode: "search" | "select";
  uiStrings: DropdownMultiTypeaheadUiStrings;
};

export function DropdownMultiTypeahead<T>({
  selected,
  onChange,
  fetchOptions,
  options,
  onSearchChange,
  getOptionLabel,
  getOptionValue,
  placeholder,
  className = "",
  error = false,
  hideSelected = false,
  disabled = false,
  onCreate,
  groupBy,
  inputMode = "select",
  uiStrings,
}: DropdownMultiTypeaheadProps<T>) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (query.length < 2 && !options) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }
    if (fetchOptions) {
      let active = true;
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const data = await fetchOptions(query);
          if (active) setSuggestions(data);
        } finally {
          if (active) setIsLoading(false);
        }
      };
      void fetchData();
      return () => {
        active = false;
      };
    }
    if (options && !query) {
      setSuggestions(options);
      setIsLoading(false);
    } else if (options) {
      const filtered = options.filter((item) =>
        removeAccents(getOptionLabel(item).toLowerCase()).includes(
          removeAccents(query.toLowerCase()),
        ),
      );
      setSuggestions(filtered);
      setIsLoading(false);
    }
  }, [query, options, fetchOptions, getOptionLabel]);

  const handleRemove = (item: T) => {
    const newSelected = selected.filter(
      (sel) => getOptionValue(sel) !== getOptionValue(item),
    );
    onChange(newSelected);
  };

  const handleSelect = (item: T) => {
    if (!selected.some((sel) => getOptionValue(sel) === getOptionValue(item))) {
      const newSelected = [...selected, item];
      onChange(newSelected);
      setQuery("");
      inputRef.current?.focus();
    }
  };

  const handleCreate = async (e: MouseEvent) => {
    e.preventDefault();
    if (onCreate && query.length > 2) {
      setIsCreating(true);
      try {
        await onCreate(query);
        setQuery("");
        inputRef.current?.focus();
      } finally {
        setIsCreating(false);
      }
    }
  };

  const availableSuggestions = suggestions.filter(
    (item) =>
      !selected.some((sel) => getOptionValue(sel) === getOptionValue(item)),
  );

  const groupedSuggestions = groupBy
    ? availableSuggestions.reduce(
        (acc, item) => {
          const group = groupBy(item);
          if (!acc[group]) acc[group] = [];
          acc[group].push(item);
          return acc;
        },
        {} as Record<string, T[]>,
      )
    : {};

  const sortedGroups = Object.keys(groupedSuggestions).sort();

  return (
    <div className={`relative ${className}`}>
      {selected.length > 0 && !hideSelected && (
        <div className="mb-1 flex flex-wrap gap-2 overflow-x-auto md:space-x-2 md:overflow-x-auto md:scrollbar-thin md:scrollbar-thumb-secondary/20">
          {selected.map((item) => (
            <button
              key={getOptionValue(item)}
              type="button"
              className="flex w-full items-center justify-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 font-ambit text-sm font-semibold text-primary transition-colors hover:bg-primary/10 focus:outline-none md:w-auto"
              onClick={() => handleRemove(item)}
              aria-label={uiStrings.removeAria}
            >
              <span>{getOptionLabel(item)}</span>
              <XCircle className="ml-1 h-4 w-4 stroke-primary/50 hover:stroke-primary" />
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`mt-2 min-h-[2.75rem] w-full cursor-pointer rounded-2xl border px-4 py-2 font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] transition-colors placeholder:text-secondary/40 focus:outline-none ${
            error
              ? "border-[#F04438] bg-[#FEE4E2]"
              : "border-primary/20 bg-[#F0F5F7] focus:border-primary focus:ring-2 focus:ring-primary/10"
          } ${disabled || isCreating ? "cursor-not-allowed opacity-50" : ""}`}
          value={query}
          onChange={(e) => {
            const value = e.target.value;
            setQuery(value);
            onSearchChange?.(value);
            setShowSuggestions(true);
          }}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          style={{ paddingRight: "2.5rem" }}
          disabled={disabled || isCreating}
        />
        {((inputMode === "select" && !!options) ||
          (inputMode === "search" && !!fetchOptions)) &&
          !query && (
            <button
              type="button"
              className="absolute inset-y-0 right-3 mt-2 flex items-center"
              onMouseDown={(e) => {
                if (inputMode === "select") {
                  e.preventDefault();
                  setShowSuggestions((s) => !s);
                  inputRef.current?.focus();
                }
              }}
              aria-label="Toggle suggestions"
            >
              <ChevronDown
                className={`h-4 w-4 text-secondary/40 transition-transform duration-200 ${showSuggestions ? "rotate-180" : ""}`}
              />
            </button>
          )}
      </div>

      {showSuggestions &&
        (query.length > 0 || (inputMode === "select" && !!options)) && (
          <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-2xl border border-primary/15 bg-white shadow-[0_8px_24px_rgba(13,45,57,0.12)]">
            {isLoading && (
              <li className="px-4 py-2.5 font-ambit text-[0.9375rem] italic text-secondary/40">
                {uiStrings.loading}
              </li>
            )}

            {!isLoading && availableSuggestions.length === 0 && (
              <>
                {(!onCreate || query.length <= 2) && (
                  <li className="px-4 py-2.5 text-center font-ambit text-[0.9375rem] italic text-secondary/40">
                    {uiStrings.noResults}
                  </li>
                )}

                {onCreate && query.length > 2 && (
                  <li
                    className="flex cursor-pointer items-center gap-2 border-b border-primary/10 px-4 py-2.5 font-ambit text-[0.9375rem] text-secondary transition-colors last:border-b-0 hover:bg-primary/5"
                    onMouseDown={handleCreate}
                  >
                    {isCreating ? (
                      <span className="italic text-secondary/40">
                        {uiStrings.saving}...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 rounded-xl border border-primary/15 bg-primary/5 px-2 py-1 transition-colors hover:border-primary">
                        <PlusCircle className="h-4 w-4 text-primary" />
                        <span>
                          {uiStrings.formAdd} &ldquo;{query}&rdquo;
                        </span>
                      </span>
                    )}
                  </li>
                )}
              </>
            )}

            {!isLoading &&
              (groupBy
                ? sortedGroups.map((group) => (
                    <Fragment key={group}>
                      <li className="sticky top-0 border-b border-primary/10 bg-secondary/5 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-secondary/40">
                        {group}
                      </li>
                      {groupedSuggestions[group].map((item) => (
                        <li
                          key={getOptionValue(item)}
                          className="cursor-pointer border-b border-primary/10 px-4 py-2.5 pl-6 font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] text-secondary transition-colors last:border-b-0 hover:bg-primary/5 hover:text-primary"
                          onMouseDown={() => handleSelect(item)}
                        >
                          {getOptionLabel(item)}
                        </li>
                      ))}
                    </Fragment>
                  ))
                : availableSuggestions.map((item) => (
                    <li
                      key={getOptionValue(item)}
                      className="cursor-pointer border-b border-primary/10 px-4 py-2.5 font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] text-secondary transition-colors last:border-b-0 hover:bg-primary/5 hover:text-primary"
                      onMouseDown={() => handleSelect(item)}
                    >
                      {getOptionLabel(item)}
                    </li>
                  )))}
          </ul>
        )}
    </div>
  );
}
